import React, { useState, useEffect } from 'react';
import { boardsAPI, tasksAPI, User, Board } from '../api/api';
import './Modal.css';

interface BoardMembersModalProps {
  board: Board;
  onClose: () => void;
  onMembersUpdated: () => void;
}

const BoardMembersModal: React.FC<BoardMembersModalProps> = ({ board, onClose, onMembersUpdated }) => {
  const [members, setMembers] = useState<Array<{ id: number; username: string; email: string }>>(
    board.members || []
  );
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadUsers();
  }, []);

  // Синхронизируем список участников с доской при обновлении
  useEffect(() => {
    if (board.members) {
      setMembers(board.members);
    }
  }, [board.members]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const data = await tasksAPI.getUsers();
      // Исключаем текущих участников и создателя доски из списка доступных для добавления
      const availableUsers = data.filter(
        user => 
          !members.some(m => m.id === user.id) && 
          user.id !== board.created_by
      );
      setUsers(availableUsers);
    } catch (err) {
      console.error('Ошибка при загрузке пользователей:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleAddMember = async (userId: number) => {
    setLoading(true);
    setError('');

    try {
      const newMember = await boardsAPI.addMember(board.id, userId);
      setMembers([...members, newMember]);
      // Обновляем список доступных пользователей
      const updatedUsers = users.filter(user => user.id !== userId);
      setUsers(updatedUsers);
      setSearchQuery('');
      onMembersUpdated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при добавлении участника');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveMember = async (userId: number) => {
    if (!window.confirm('Вы уверены, что хотите удалить этого участника из доски?')) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      await boardsAPI.removeMember(board.id, userId);
      const removedMember = members.find(m => m.id === userId);
      setMembers(members.filter(m => m.id !== userId));
      
      // Добавляем пользователя обратно в список доступных, если он не создатель
      if (removedMember && userId !== board.created_by) {
        setUsers([...users, {
          id: removedMember.id,
          username: removedMember.username,
          email: removedMember.email,
          role: 'user',
          approved: true
        }]);
      }
      
      onMembersUpdated();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Ошибка при удалении участника');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => {
    const query = searchQuery.toLowerCase();
    return (
      user.username.toLowerCase().includes(query) ||
      (user.email && user.email.toLowerCase().includes(query))
    );
  });

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
        <div className="modal-header">
          <h2>Участники доски</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="error" style={{ margin: '0 20px 15px' }}>{error}</div>}

        <div style={{ padding: '0 20px' }}>
          {/* Текущие участники */}
          <div style={{ marginBottom: '25px' }}>
            <h3 style={{ marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
              Текущие участники ({members.length})
            </h3>
            {members.length === 0 ? (
              <div style={{ padding: '15px', backgroundColor: '#f5f5f5', borderRadius: '4px', color: '#666' }}>
                Нет участников (кроме создателя)
              </div>
            ) : (
              <div className="users-list">
                {members.map(member => (
                  <div key={member.id} className="user-item" style={{ padding: '12px' }}>
                    <div className="user-info" style={{ flex: 1 }}>
                      <span className="user-name">{member.username}</span>
                      {member.email && <span className="user-email">{member.email}</span>}
                    </div>
                    <button
                      onClick={() => handleRemoveMember(member.id)}
                      disabled={loading}
                      className="remove-member-btn"
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#ff4444',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: loading ? 'not-allowed' : 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      Удалить
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Создатель доски */}
          <div style={{ marginBottom: '25px', padding: '12px', backgroundColor: '#e8f5e9', borderRadius: '4px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontWeight: '600' }}>Создатель доски:</span>
              <span>{board.created_by_username || 'Неизвестно'}</span>
              <span style={{ 
                padding: '2px 8px', 
                backgroundColor: '#4caf50', 
                color: 'white', 
                borderRadius: '12px', 
                fontSize: '11px' 
              }}>
                Создатель
              </span>
            </div>
          </div>

          {/* Добавление новых участников */}
          <div>
            <h3 style={{ marginBottom: '10px', fontSize: '16px', fontWeight: '600' }}>
              Добавить участника
            </h3>
            {loadingUsers ? (
              <div className="users-loading">Загрузка пользователей...</div>
            ) : (
              <>
                <div className="users-search-container">
                  <input
                    type="text"
                    className="users-search"
                    placeholder="Поиск по имени или email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </div>
                <div className="users-list" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {filteredUsers.length === 0 ? (
                    <div className="users-empty">
                      {searchQuery ? 'Ничего не найдено' : 'Нет доступных пользователей для добавления'}
                    </div>
                  ) : (
                    filteredUsers.map(user => (
                      <div
                        key={user.id}
                        className="user-item"
                        onClick={() => handleAddMember(user.id)}
                        style={{
                          cursor: loading ? 'not-allowed' : 'pointer',
                          opacity: loading ? 0.6 : 1
                        }}
                      >
                        <div className="user-info">
                          <span className="user-name">{user.username}</span>
                          {user.email && <span className="user-email">{user.email}</span>}
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddMember(user.id);
                          }}
                          disabled={loading}
                          className="add-member-btn"
                          style={{
                            padding: '6px 12px',
                            backgroundColor: '#4caf50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: loading ? 'not-allowed' : 'pointer',
                            fontSize: '13px'
                          }}
                        >
                          Добавить
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: '20px', padding: '15px 20px', borderTop: '1px solid #eee' }}>
          <button type="button" className="submit-btn" onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  );
};

export default BoardMembersModal;

